// "When did it go offline" — the drop log for the Tuya bench, both shells.
//
// The card's liveness chip answers "is it here NOW". This answers the question
// that actually gets asked on a bench: *when did it stop*, and *how long was it
// gone*. Those are different questions, and the second one cannot be answered by
// looking at a screen later — nothing on the device records it, so if the app
// does not stamp the transition as it happens, the answer is gone.
//
// ⚠️ THE DROP TIME IS THE LAST SIGHTING, NOT THE MOMENT WE NOTICED.
//
// Silence is only detectable after the fact: a device that goes quiet at 13:20:00
// cannot be distinguished from a healthy one until its next heartbeat fails to
// arrive, three and a half minutes later. Reporting 13:23:30 would be wrong by
// exactly one beat, every single time — and the whole point of the log is the
// timestamp. So the drop is stamped at the last frame actually received, and
// "noticed" is shown separately for anyone checking the app's own latency.

import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Paper, Chip,
} from '@mui/material';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { shortAge } from '../../../core/devices/tuyaTesting';
import { FIT_MD } from '../fitDialog';

const clock = (ms) => (ms == null ? '—'
  : new Date(ms).toLocaleTimeString(undefined, { hour12: false }));

const day = (ms) => (ms == null ? ''
  : new Date(ms).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }));

export default function DropLogDialog({ open, events = [], onClear, onClose }) {
  const drops = events.filter((e) => e.kind === 'drop').length;

  return (
    <Dialog open={open} onClose={onClose} {...FIT_MD}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
        <LinkOffIcon />
        <Box>
          <Typography component="div" sx={{ fontSize: 20, fontWeight: 700, lineHeight: 1.2 }}>
            Connection log
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {`${drops} drop${drops === 1 ? '' : 's'} since this screen was opened`}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {events.length === 0 ? (
          <Box sx={{ py: 3 }}>
            <Typography variant="body2" color="text.secondary">
              Nothing has dropped or come back yet. This fills in as devices go
              quiet — it only covers the time this screen has been open, because
              nothing records the transition but this.
            </Typography>
          </Box>
        ) : (
          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Device</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Event</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Time</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Detail</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {events.map((e) => (
                  <TableRow key={`${e.uno}-${e.kind}-${e.at}`}>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12.5 }}>{e.uno}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={e.kind === 'drop' ? 'Went quiet' : 'Came back'}
                        color={e.kind === 'drop' ? 'error' : 'success'}
                        variant={e.kind === 'drop' ? 'filled' : 'outlined'}
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                        {clock(e.at)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{day(e.at)}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">
                        {e.kind === 'drop'
                          ? `last frame received — noticed ${clock(e.noticedAt)}`
                          : (e.downFor != null
                            ? `down for ${shortAge(e.downFor)}`
                            : 'no matching drop in this session')}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClear} disabled={events.length === 0}>Clear</Button>
        <Button variant="contained" onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
